/**
 * The five workspaces a signed-in user can land in.
 *
 * These are NOT stored on a user and are never sent to the server as a claim.
 * Each is DERIVED from a grant that exists for another reason — you are a
 * collector because you own work, a gallery user because you hold a
 * membership, an agent because someone granted you a mandate. Treating a role
 * as input would make the workspace switcher a privilege-escalation surface.
 */
export const ROLE_KEYS = [
    "collector",
    "estate",
    "agent",
    "gallery",
    "verifier",
];
/**
 * The organisation types a signed-in caller may create for themselves.
 *
 * An ENTITY, in OND-40's answer of 12 October 2026, is "a separate legal
 * owner"; the types are person, company, trust and foundation. A person's
 * entity is their own principal and needs no creating, so these three are what
 * `POST /v1/orgs` accepts.
 *
 * THE LIST IS THE AUTHORIZATION, and it is a mirror of the same list inside
 * `ondo.create_entity()` rather than a second opinion — the database refuses
 * every other value and this exists so a screen offers only what it will take.
 * The eight types above are absent because each confers a CAPACITY through
 * `ondo.capacity_for_org_type()`: a self-minted `advisory` would be an account
 * making itself assignable as an agent, which is the objection
 * `apps/local-admin-cli/src/lib/organisations.ts` raised against a front door
 * ever existing. These three map to nothing, confer nothing, and open no
 * workspace, which is why the objection does not reach them.
 *
 * Must stay in step with the type list in `ondo.create_entity()`. Nothing
 * enforces that but this comment and `supabase/tests/rls.sh`.
 */
export const ENTITY_ORG_TYPES = ["company", "trust", "foundation"];
export function isEntityOrgType(value) {
    return typeof value === "string" && ENTITY_ORG_TYPES.includes(value);
}
/**
 * The rungs, in order, lowest first.
 *
 * Must stay in step with the `ondo.membership_level` enum AND with
 * `ondo.principal_ids_at_least()`, which is what every policy actually asks.
 * Nothing enforces that but this comment and `atLeast` below.
 */
export const MEMBERSHIP_LEVELS = ["viewer", "editor", "admin"];
/**
 * Whether a member at `level` reaches `required`.
 *
 * THE CLIENT-SIDE HALF OF `ondo.principal_ids_at_least()`, and it is a MIRROR
 * rather than a second opinion. The database decides, always; this exists so a
 * screen can decline to offer a control the database is certain to refuse.
 *
 * That distinction is the whole reason it is safe. Reading a rung to hide a
 * button is not the "role check in application code" CLAUDE.md bans — the rung
 * is server-supplied on `GET /v1/me`, the button being present grants nothing,
 * and the refusal still comes from Postgres if anyone reaches the route another
 * way. What it prevents is a registrar pressing Remit and being handed a raw
 * 42501, which reads as a fault rather than as the answer it is.
 *
 * `null` means a PERSONAL workspace: there is no firm and therefore no rung,
 * and the caller acts as themselves. Every such act is bounded by ownership
 * instead, which the capabilities already carry — so the answer is yes, and a
 * screen that wanted otherwise would be asking the wrong question.
 */
export function atLeast(level, required) {
    if (level === null || level === undefined)
        return true;
    return MEMBERSHIP_LEVELS.indexOf(level) >= MEMBERSHIP_LEVELS.indexOf(required);
}
/**
 * What a seat at a firm may do, where a named role's bundle differs from the
 * seat's rung.
 *
 * FOUR VALUES, one per place the owner's four bundles disagree with the three
 * rungs — OND-61 decision 1. Catalogue writes, history contribution, document
 * uploads and evidence reads are not here, and their absence is the decision:
 * every bundle that has them sits at a rung that already gives them, so a value
 * for one would be a privilege no door reads. A fifth belongs here when a door
 * reads it and not before.
 *
 * Must stay in step with the `ondo.org_privilege` enum in supabase/migrations.
 * Nothing enforces that but this comment.
 */
export const ORG_PRIVILEGES = [
    /** Read the firm's collector list — and therefore export it, and read one contact's timeline. */
    "collector_list_read",
    /**
     * Confirm or dispute, in the firm's name, information ONDO holds.
     *
     * The design calls this "can sign off" and "attribution confirmation", and it
     * is ONE privilege rather than two because the confirmation flow does not
     * branch on what kind of history it is confirming — see CLAUDE.md, where that
     * absence is named as the feature. A privilege that applied to an authorship
     * claim and not to a sale would be the first `switch` on `kind` in that path.
     */
    "history_confirm",
    /** Issue a share code in the firm's name, and revoke one issued in it. */
    "share_code_issue",
    /** Administer the firm's staff: end a seat, set a role, edit a bundle. */
    "staff_admin",
];
export function isOrgPrivilege(value) {
    return typeof value === "string" && ORG_PRIVILEGES.includes(value);
}
/**
 * A bundle of privileges, shown on the roster and enforced in the database.
 *
 * NOT a rung. The three rungs stay underneath as the coarse mechanism, and a
 * role may re-scope an ability above or below its seat's rung within the floors
 * `ondo.privilege_floor()` sets — which is what lets an Authentication
 * Specialist at the viewer rung confirm an attribution.
 *
 * `null` on a seat means no bundle, which is every seat that existed before
 * OND-61: the rung answers alone.
 *
 * Must stay in step with the `ondo.org_role` enum in supabase/migrations.
 */
export const ORG_ROLES = ["director", "registrar", "scholar", "specialist"];
export function isOrgRole(value) {
    return typeof value === "string" && ORG_ROLES.includes(value);
}
/**
 * Whether a seat holds a privilege.
 *
 * THE CLIENT-SIDE HALF OF `ondo.seat_holds()`, and a MIRROR rather than a
 * second opinion — exactly as `atLeast` above is a mirror of
 * `ondo.principal_ids_at_least()`, and safe for the reason stated there: the
 * list is server-supplied on `GET /v1/me`, the control being present grants
 * nothing, and the refusal still comes from Postgres if anyone reaches the
 * route another way.
 *
 * It takes the RESOLVED list and never a role, because resolving a bundle on
 * the client would be a second definition of the privilege — wrong the moment a
 * firm edits a bundle or a seat carries an exception, which is now the ordinary
 * case rather than the exotic one.
 *
 * `null` or `undefined` means a PERSONAL workspace: no firm, no rung, no
 * bundle, and every act bounded by ownership instead. The answer is yes, for
 * the same reason `atLeast(null)` is yes.
 */
export function holdsPrivilege(privileges, required) {
    if (privileges === null || privileges === undefined)
        return true;
    return privileges.includes(required);
}
//# sourceMappingURL=roles.js.map